import React, {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react'
import styled from 'styled-components'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  $error?: boolean
  // Grows with its content between `minRows` (default 1) and `maxRows`
  // (default unbounded); past `maxRows` it scrolls.
  autoSize?: boolean | { minRows?: number; maxRows?: number }
}

const LINE_HEIGHT = 20

function pixels(value: string): number {
  return parseFloat(value) || 0
}

// Base UI has no textarea part, so this stays a native <textarea> (and does
// not join a Field.Root). `className` stays on the <textarea> so
// `styled(TextArea)` restyles it directly.
const TextAreaImpl = React.forwardRef<
  HTMLTextAreaElement,
  Omit<TextAreaProps, '$error'>
>(function TextAreaImpl({ autoSize, onChange, value, ...rest }, ref) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement)
  const enabled = Boolean(autoSize)
  const { minRows = 1, maxRows } = typeof autoSize === 'object' ? autoSize : {}

  const resize = useCallback((): void => {
    const textarea = textareaRef.current
    if (!enabled || !textarea) return
    const computed = getComputedStyle(textarea)
    const lineHeight = pixels(computed.lineHeight) || LINE_HEIGHT
    const borderY =
      pixels(computed.borderTopWidth) + pixels(computed.borderBottomWidth)
    const chromeY =
      borderY + pixels(computed.paddingTop) + pixels(computed.paddingBottom)

    textarea.style.height = 'auto'
    const wanted = textarea.scrollHeight + borderY
    const min = minRows * lineHeight + chromeY
    const max =
      maxRows === undefined ? Infinity : maxRows * lineHeight + chromeY
    textarea.style.height = `${Math.min(Math.max(wanted, min), max)}px`
    textarea.style.overflowY = wanted > max ? 'auto' : 'hidden'
  }, [enabled, minRows, maxRows])

  useLayoutEffect(resize, [resize, value])

  return (
    <textarea
      // A native textarea is two rows tall at minimum, which would floor
      // `scrollHeight` and make `minRows={1}` unreachable.
      rows={enabled ? 1 : undefined}
      {...rest}
      ref={textareaRef}
      value={value}
      data-autosize={enabled ? '' : undefined}
      onChange={(event) => {
        resize()
        onChange?.(event)
      }}
    />
  )
})

// Mirrors Input's visual language (border, radius, placeholder, hover,
// focus ring) for the multi-line case Input's `<input>` tag can't cover —
// an `<input>` has no `rows` and no line wrapping.
export const TextArea = styled(TextAreaImpl)<TextAreaProps>`
  border-width: 1.5px;
  border-style: solid;
  border-color: ${({ $error }) =>
    $error ? accents.crimsonBloom[500] : '#D5D7DA'};
  border-radius: ${radii.sm}px;
  background: ${brand.white};
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: 14px;
  line-height: ${LINE_HEIGHT}px;
  color: ${brand.stillNight[800]};
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  resize: vertical;
  transition:
    background-color ${motion.duration.base}ms ${motion.ease},
    border-color ${motion.duration.base}ms ${motion.ease},
    box-shadow ${motion.duration.base}ms ${motion.ease};

  &[data-autosize] {
    resize: none;
  }

  &::placeholder {
    color: #a4a7ae;
  }

  &:hover:not(:disabled):not(:focus) {
    background: ${gray[100]};
  }

  &:focus {
    outline: none;
    border-color: ${brand.stillNight[800]};
    box-shadow: 0 0 0 4px rgba(40, 54, 79, 0.16);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

TextArea.displayName = 'TextArea'
